import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Role } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import { Modal } from '../components/Modal.tsx';
import { TabPanel, Tabs } from '../components/Tabs.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Table,
} from '../components/ui.tsx';
import { localized } from '../i18n/index.ts';
import {
  areaNameKey,
  areasWith,
  codesOf,
  PERMISSION_AREAS,
  permissionDefinition,
  processNameKey,
  PROFILE_TEMPLATES,
  roleCodeFromName,
  templatePermissions,
  withinAreas,
  type ProfileTemplate,
} from '../permission-areas.ts';

/** Mesma regra do servidor (`shortCode`): 2 a 30 letras maiúsculas, números, - ou _. */
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{1,29}$/;

/**
 * Criar e editar perfil em etapas, no estilo do Bankline Itaú: dados e tipo, departamentos,
 * funcionalidades (por processo, com "selecionar todos" e contador) e confirmação. O perfil
 * continua sendo só a lista de permissões; o tipo apenas pré-marca as funcionalidades.
 */
type Step = 'data' | 'areas' | 'functions' | 'review';
const STEPS: readonly Step[] = ['data', 'areas', 'functions', 'review'];

type Form = {
  code: string;
  namePt: string;
  nameEn: string;
  descPt: string;
  descEn: string;
  template: ProfileTemplate;
  areas: Set<string>;
  permissions: Set<string>;
  isActive: boolean;
};

function toForm(r: Role | null): Form {
  return {
    code: r?.code ?? '',
    namePt: r?.name['pt-BR'] ?? '',
    nameEn: r?.name.en ?? '',
    descPt: r?.description['pt-BR'] ?? '',
    descEn: r?.description.en ?? '',
    template: 'custom',
    areas: new Set(r ? areasWith(r.permissions) : []),
    permissions: new Set(r?.permissions ?? []),
    isActive: r?.isActive ?? true,
  };
}

/** O que vai para o servidor: serve também para saber se há algo alterado. */
function payload(form: Form) {
  return {
    code: form.code.trim().toUpperCase(),
    name: { 'pt-BR': form.namePt, en: form.nameEn },
    description: { 'pt-BR': form.descPt, en: form.descEn },
    permissions: [...form.permissions].sort(),
    isActive: form.isActive,
  };
}

/** Caixa de "selecionar todos": marcada, desmarcada ou pela metade. */
function SelectAll({
  codes,
  selected,
  disabled,
  label,
  onChange,
}: {
  codes: readonly string[];
  selected: Set<string>;
  disabled?: boolean;
  label: string;
  onChange: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const count = codes.filter((c) => selected.has(c)).length;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = count > 0 && count < codes.length;
  }, [count, codes.length]);
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        checked={codes.length > 0 && count === codes.length}
        onChange={(ev) => onChange(ev.target.checked)}
      />
      {label}
    </label>
  );
}

const counter = (codes: readonly string[], selected: Set<string>) =>
  `(${codes.filter((c) => selected.has(c)).length}/${codes.length})`;

function RoleWizard({ role, onClose }: { role: Role | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [initial] = useState(() => toForm(role));
  const [form, setForm] = useState<Form>(initial);
  const [step, setStep] = useState<Step>('data');
  const [codeTouched, setCodeTouched] = useState(!!role);
  const [applied, setApplied] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const locked = !!role?.isSystem;
  const dirty = JSON.stringify(payload(form)) !== JSON.stringify(payload(initial));

  const save = useMutation({
    mutationFn: () =>
      role
        ? api.put<Role>(`/iam/roles/${role.id}`, payload(form))
        : api.post<Role>('/iam/roles', payload(form)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['roles'] });
      await qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const dataOk =
    CODE_PATTERN.test(form.code.trim().toUpperCase()) &&
    form.namePt.trim() !== '' &&
    form.nameEn.trim() !== '';
  const areasOk = locked || form.areas.size > 0;
  const reachable = (s: Step) => {
    const i = STEPS.indexOf(s);
    return (i < 1 || dataOk) && (i < 2 || areasOk);
  };

  /** Ao chegar nas funcionalidades, o tipo escolhido pré-marca o que ele prevê (perfil novo). */
  const go = (target: Step) => {
    if (!reachable(target)) return;
    if (!role && target !== 'data' && target !== 'areas' && form.template !== 'custom') {
      const signature = `${form.template}|${[...form.areas].sort().join(',')}`;
      if (signature !== applied) {
        setApplied(signature);
        setForm({ ...form, permissions: templatePermissions(form.template, form.areas) });
      }
    }
    setStep(target);
  };
  const index = STEPS.indexOf(step);

  const text =
    (k: 'code' | 'namePt' | 'nameEn' | 'descPt' | 'descEn') =>
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const value = ev.target.value;
      if (k === 'code') setCodeTouched(true);
      setForm({
        ...form,
        [k]: value,
        ...(k === 'namePt' && !codeTouched ? { code: roleCodeFromName(value) } : {}),
      });
    };
  const setMany = (codes: readonly string[], on: boolean) => {
    const next = new Set(form.permissions);
    for (const c of codes) {
      if (on) next.add(c);
      else next.delete(c);
    }
    setForm({ ...form, permissions: next });
  };
  const toggleArea = (id: string, on: boolean) => {
    const areas = new Set(form.areas);
    if (on) areas.add(id);
    else areas.delete(id);
    setForm({ ...form, areas, permissions: withinAreas(form.permissions, areas) });
  };
  const chooseTemplate = (template: ProfileTemplate) => {
    setForm({ ...form, template });
  };
  const toggleOpen = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };

  const chosenAreas = PERMISSION_AREAS.filter((a) => form.areas.has(a.id));
  const chosenCodes = chosenAreas.flatMap(codesOf);
  const shown = [...form.permissions];
  const sensitiveCount = shown.filter((c) => permissionDefinition(c)?.sensitive).length;

  const dataStep = (
    <>
      {locked && <p className="text-sm text-muted">{t('roles.systemNote')}</p>}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">{t('roles.wizard.data.basics')}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('roles.namePt')} required>
            <Input id="role-name-pt" value={form.namePt} onChange={text('namePt')} />
          </Field>
          <Field label={t('roles.nameEn')} required>
            <Input id="role-name-en" value={form.nameEn} onChange={text('nameEn')} />
          </Field>
          <Field label={t('roles.descPt')} hint={t('roles.wizard.data.descHint')}>
            <Input id="role-desc-pt" value={form.descPt} onChange={text('descPt')} />
          </Field>
          <Field label={t('roles.descEn')}>
            <Input id="role-desc-en" value={form.descEn} onChange={text('descEn')} />
          </Field>
          <Field
            label={t('fields.code')}
            required
            hint={role ? t('fields.codeHint') : t('roles.wizard.data.codeHint')}
          >
            <Input id="role-code" disabled={locked} value={form.code} onChange={text('code')} />
          </Field>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input
              id="role-active"
              type="checkbox"
              disabled={locked}
              checked={form.isActive}
              onChange={(ev) => setForm({ ...form, isActive: ev.target.checked })}
            />
            {t('fields.active')}
          </label>
        </div>
      </section>
      {!role && (
        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-semibold">{t('roles.wizard.types.title')}</legend>
          <p className="text-xs text-muted">{t('roles.wizard.types.hint')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PROFILE_TEMPLATES.map((id) => (
              <label
                key={id}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${
                  form.template === id ? 'border-accent bg-brand-soft' : 'border-rule'
                }`}
              >
                <input
                  type="radio"
                  name="role-template"
                  className="mt-1"
                  checked={form.template === id}
                  onChange={() => chooseTemplate(id)}
                />
                <span>
                  <span className="font-medium">{t(`roles.wizard.types.${id}.name`)}</span>
                  <span className="block text-xs text-muted">
                    {t(`roles.wizard.types.${id}.description`)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </>
  );

  const areasStep = (
    <fieldset className="flex flex-col gap-3" disabled={locked}>
      <legend className="text-sm font-semibold">{t('roles.wizard.areas.title')}</legend>
      <p className="text-xs text-muted">{t('roles.wizard.areas.hint')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {PERMISSION_AREAS.map((area) => {
          const codes = codesOf(area);
          return (
            <label
              key={area.id}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${
                form.areas.has(area.id) ? 'border-accent bg-brand-soft' : 'border-rule'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={form.areas.has(area.id)}
                onChange={(ev) => toggleArea(area.id, ev.target.checked)}
              />
              <span>
                <span className="font-medium">{t(areaNameKey(area))}</span>
                <span className="block text-xs text-muted">
                  {area.processes.map((p) => t(processNameKey(p))).join(' · ')}
                </span>
                {form.areas.has(area.id) && (
                  <span className="block text-xs text-muted">
                    {t('roles.wizard.areas.marked', {
                      count: codes.filter((c) => form.permissions.has(c)).length,
                      total: codes.length,
                    })}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {!areasOk && <p className="text-xs text-danger">{t('roles.wizard.areas.none')}</p>}
    </fieldset>
  );

  const functionsStep = (
    <fieldset className="flex flex-col gap-4" disabled={locked}>
      <legend className="sr-only">{t('roles.wizard.steps.functions')}</legend>
      <p className="text-xs text-muted">
        {!role && form.template !== 'custom'
          ? t('roles.wizard.functions.prefilled', {
              type: t(`roles.wizard.types.${form.template}.name`),
            })
          : t('roles.wizard.functions.hint')}
      </p>
      <SelectAll
        codes={chosenCodes}
        selected={form.permissions}
        label={`${t('roles.wizard.functions.selectEverything')} ${counter(chosenCodes, form.permissions)}`}
        onChange={(on) => setMany(chosenCodes, on)}
      />
      {chosenAreas.map((area) => {
        const codes = codesOf(area);
        return (
          <section key={area.id} className="flex flex-col gap-2 rounded-lg border border-rule p-3">
            <h3 className="text-sm font-semibold">
              {t(areaNameKey(area))} {counter(codes, form.permissions)}
            </h3>
            <SelectAll
              codes={codes}
              selected={form.permissions}
              label={t('roles.wizard.functions.selectAll')}
              onChange={(on) => setMany(codes, on)}
            />
            {area.processes.map((process) => {
              const key = `${area.id}.${process.id}`;
              const expanded = open.has(key);
              const panelId = `role-process-${area.id}-${process.id}`;
              return (
                <div key={process.id} className="rounded-md bg-paper">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => toggleOpen(key)}
                    className="flex w-full items-center gap-2 px-2 py-2 text-left text-sm font-medium"
                  >
                    {expanded ? (
                      <ChevronDown aria-hidden className="size-4" />
                    ) : (
                      <ChevronRight aria-hidden className="size-4" />
                    )}
                    {t(processNameKey(process))}{' '}
                    <span className="text-muted">
                      {counter(process.permissions, form.permissions)}
                    </span>
                  </button>
                  {expanded && (
                    <div id={panelId} className="flex flex-col gap-2 px-8 pb-3">
                      <SelectAll
                        codes={process.permissions}
                        selected={form.permissions}
                        label={t('roles.wizard.functions.selectAll')}
                        onChange={(on) => setMany(process.permissions, on)}
                      />
                      {process.permissions.map((code) => {
                        const def = permissionDefinition(code);
                        return (
                          <label key={code} className="flex items-start gap-2 text-sm" title={code}>
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={form.permissions.has(code)}
                              onChange={(ev) => setMany([code], ev.target.checked)}
                            />
                            <span>
                              {def ? localized(def.label) : code}
                              {def?.sensitive && (
                                <span className="ml-2">
                                  <Badge tone="info">{t('roles.wizard.sensitive')}</Badge>
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </fieldset>
  );

  const reviewStep = (
    <>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-muted">{t('fields.name')}</dt>
        <dd>
          {form.namePt} <span className="text-muted">/ {form.nameEn}</span>
        </dd>
        <dt className="text-muted">{t('fields.code')}</dt>
        <dd className="font-mono">{form.code.trim().toUpperCase()}</dd>
        {form.descPt && (
          <>
            <dt className="text-muted">{t('roles.descPt')}</dt>
            <dd>{form.descPt}</dd>
          </>
        )}
        <dt className="text-muted">{t('fields.status')}</dt>
        <dd>{form.isActive ? t('fields.active') : t('fields.inactive')}</dd>
        <dt className="text-muted">{t('roles.permissions')}</dt>
        <dd>
          {t('roles.permissionCount', { count: shown.length })}
          {sensitiveCount > 0 && (
            <span className="ml-2">
              <Badge tone="info">
                {t('roles.wizard.review.sensitiveCount', { count: sensitiveCount })}
              </Badge>
            </span>
          )}
        </dd>
      </dl>
      {shown.length === 0 ? (
        <p className="text-sm text-danger">{t('roles.wizard.review.empty')}</p>
      ) : (
        PERMISSION_AREAS.map((area) => {
          const processes = area.processes.filter((p) =>
            p.permissions.some((c) => form.permissions.has(c)),
          );
          if (processes.length === 0) return null;
          return (
            <section
              key={area.id}
              className="flex flex-col gap-2 rounded-lg border border-rule p-3"
            >
              <h3 className="text-sm font-semibold">
                {t(areaNameKey(area))} {counter(codesOf(area), form.permissions)}
              </h3>
              {processes.map((p) => (
                <div key={p.id} className="text-sm">
                  <span className="font-medium">{t(processNameKey(p))}</span>
                  <ul className="mt-1 ml-5 list-disc">
                    {p.permissions
                      .filter((c) => form.permissions.has(c))
                      .map((c) => {
                        const def = permissionDefinition(c);
                        return (
                          <li key={c}>
                            {def ? localized(def.label) : c}
                            {def?.sensitive && (
                              <span className="ml-2">
                                <Badge tone="info">{t('roles.wizard.sensitive')}</Badge>
                              </span>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                </div>
              ))}
            </section>
          );
        })
      )}
      {sensitiveCount > 0 && (
        <p className="text-xs text-muted">{t('roles.wizard.review.sensitiveNote')}</p>
      )}
      <p className="text-xs text-muted">{t('roles.wizard.review.next')}</p>
      <ErrorBox error={save.error} />
    </>
  );

  return (
    <Modal
      open
      size="record"
      compact
      dirty={dirty}
      onClose={onClose}
      title={role ? t('roles.edit') : t('roles.new')}
      description={role ? localized(role.name) : t('roles.wizard.intro')}
      tabs={
        <Tabs
          items={STEPS.map((id, i) => ({
            id,
            label: `${i + 1}. ${t(`roles.wizard.steps.${id}`)}`,
          }))}
          value={step}
          onChange={go}
          label={t('roles.wizard.steps.label')}
          idPrefix="rw"
        />
      }
      footer={
        <>
          <span className="mr-auto self-center text-xs text-muted">
            {step === 'data' ? t('common.requiredLegend') : dirty ? t('roles.wizard.unsaved') : ''}
          </span>
          {index > 0 && (
            <Button variant="secondary" onClick={() => go(STEPS[index - 1]!)}>
              {t('roles.wizard.back')}
            </Button>
          )}
          {step !== 'review' ? (
            <Button disabled={!reachable(STEPS[index + 1]!)} onClick={() => go(STEPS[index + 1]!)}>
              {t('roles.wizard.next')}
            </Button>
          ) : (
            <Button disabled={save.isPending || !dataOk || !areasOk} onClick={() => save.mutate()}>
              {save.isPending
                ? t('common.saving')
                : role
                  ? t('common.save')
                  : t('roles.wizard.create')}
            </Button>
          )}
        </>
      }
    >
      <TabPanel idPrefix="rw" id={step}>
        {step === 'data' && dataStep}
        {step === 'areas' && areasStep}
        {step === 'functions' && functionsStep}
        {step === 'review' && reviewStep}
      </TabPanel>
    </Modal>
  );
}

export function RolesPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const canManage = can(me, 'iam.role.manage');
  const [open, setOpen] = useState<{ role: Role | null } | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/iam/roles') });

  return (
    <>
      <PageHeader
        title={t('roles.title')}
        description={t('roles.description')}
        actions={
          canManage ? (
            <Button onClick={() => setOpen({ role: null })}>{t('roles.new')}</Button>
          ) : null
        }
      />

      {open && (
        <RoleWizard key={open.role?.id ?? 'new'} role={open.role} onClose={() => setOpen(null)} />
      )}

      {roles.isPending ? (
        <Loading />
      ) : roles.isError ? (
        <ErrorBox error={roles.error} />
      ) : (
        <Table
          head={[
            t('fields.code'),
            t('fields.name'),
            t('roles.wizard.steps.areas'),
            t('roles.permissions'),
            t('fields.status'),
            '',
          ]}
        >
          {roles.data.length === 0 && <EmptyRow colSpan={6}>{t('roles.empty')}</EmptyRow>}
          {roles.data.map((r) => (
            <tr key={r.id}>
              <td className="font-mono">
                {r.code}
                {r.isSystem && (
                  <div className="mt-1">
                    <Badge>{t('roles.system')}</Badge>
                  </div>
                )}
              </td>
              <td>
                {localized(r.name)}
                {localized(r.description) && (
                  <div className="text-xs text-muted">{localized(r.description)}</div>
                )}
              </td>
              <td className="text-xs">
                {areasWith(r.permissions)
                  .map((id) => t(areaNameKey(id)))
                  .join(', ')}
              </td>
              <td className="text-xs">
                {t('roles.permissionCount', { count: r.permissions.length })}
              </td>
              <td>
                {r.isActive ? (
                  <Badge tone="ok">{t('fields.active')}</Badge>
                ) : (
                  <Badge tone="off">{t('fields.inactive')}</Badge>
                )}
              </td>
              <td className="text-right">
                <span className="flex justify-end gap-2">
                  {canManage && (
                    <Button variant="secondary" onClick={() => setOpen({ role: r })}>
                      {t('common.edit')}
                    </Button>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
